import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import "react-confirm-alert/src/react-confirm-alert.css";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import Pagination from "../../components/AdminSideComponents/AdminPagination";
import DeleteConfirmationModal from "../../components/AdminSideComponents/Modals/DeleteConfirmationModal";
import toast from "react-hot-toast";
import AdminServiceModal from "../../components/AdminSideComponents/Modals/AdminServiceModal";
import HoverCarousel from "../../components/Products/HoverCarousel";

const Services = () => {
  const ITEMS_PER_PAGE = 6;

  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActionCompleted,setIsActionCompleted]=useState(false)

  // FETCH SERVICES
  const fetchServices = async () => {
    try {
      const response = await axiosInstance.get("/services/all");
      setServices(response.data);
      setFilteredServices(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error("Error fetching services:", err);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const newTotalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages || 1); // fallback to 1 if no products
    }
  }, [filteredServices, currentPage]);

  // SEARCH HANDLER
  const onSearchHandler = (query) => {
    setSearchTerm(query);
    const lowerCaseQuery = query.trim().toLowerCase();
    const results = services.filter((service) =>
      service.name.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredServices(results);
    setCurrentPage(1);
  };

  // DELETE HANDLERS
  const handleOpenDeleteModal = (id) => {
    setDeletingServiceId(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingServiceId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingServiceId) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/services/delete/${deletingServiceId}`);
      const updatedList = services.filter(
        (service) => service._id !== deletingServiceId
      );
      setServices(updatedList);
      setFilteredServices(
        searchTerm
          ? updatedList.filter((service) =>
              service.name
                .toLowerCase()
                .includes(searchTerm.trim().toLowerCase())
            )
          : updatedList
      );
      toast.success("Service deleted successfully", { id: deletingServiceId });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete. Try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeletingServiceId(null);
    }
  };

  const onAddNewHandler = () => {
    setEditData(null);
    setIsActionCompleted(false)
    setModalOpen(true);
  };

  // HANDLE EDIT CLICK
  const handleEditClick = (service) => {
    setEditData(service);
    setModalOpen(true);
  };

  // HANDLE SERVICE SUBMISSION (CREATE/UPDATE)
  const handleServiceSubmit = async (serviceData) => {
    setIsSubmitting(true);
    try {
      if (editData) {
        const updatedService = await axiosInstance.put(
          `/services/update/${editData._id}`,
          serviceData
        );
        setServices(
          services.map((s) =>
            s._id === editData._id ? updatedService.data : s
          )
        );
        setFilteredServices(
          filteredServices.map((s) =>
            s._id === editData._id ? updatedService.data : s
          )
        );
        toast.success("Service updated successfully");
      } else {
        const newService = await axiosInstance.post(
          "/services/create",
          serviceData
        );
        setServices([newService.data, ...services]);
        setIsActionCompleted(true)
        setFilteredServices([newService.data, ...filteredServices]);
        toast.success("Service created successfully");
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Error saving service:", err);
      toast.error(err.response?.data?.message || "Failed to save service");
    } finally {
      setIsSubmitting(false);
      fetchServices();
    }
  };

  // PAGINATION
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse h-48 mb-4"></div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Services Management"
          onSearchClick={onSearchHandler}
          onAddNewClick={onAddNewHandler}
        />
        {Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
          <SkeletonCard key={idx} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Services Management"
          onSearchClick={onSearchHandler}
          onAddNewClick={onAddNewHandler}
        />
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center justify-between">
          <div>
            <p className="font-semibold">Failed to load services</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={fetchServices}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4">
      <AdminSearchBar
        title="Services Management"
        onSearchClick={onSearchHandler}
        onAddNewClick={onAddNewHandler}
      />

      {paginatedServices.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-600">
            No services found
          </h3>
          <p className="text-gray-500 mt-2">
            Click the button above to add your first service
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {paginatedServices.map((service) => (
            <div
              key={service._id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition flex flex-col relative"
            >
              {/* IMAGE */}
              <div className="relative h-48 bg-gray-100">
                <HoverCarousel images={service.images} alt={service.name} />
              </div>

              {/* CONTENT */}
              <div className="p-4 flex-1 flex flex-col justify-between group">
                <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                  {service.description}
                </p>

                {/* BUTTONS: slide-up on hover */}
                <div
                  className="absolute bottom-0 left-0 w-full flex justify-end gap-2 px-4 transform translate-y-full opacity-0 
             bg-white bg-opacity-70 py-2 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditClick(service);
                    }}
                    className="flex items-center justify-center transition 
                              text-primaryBtnColor hover:text-primaryBtnHoverColor
                              shadow hover:shadow-md"
                  >
                    <FiEdit size={20} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenDeleteModal(service._id);
                    }}
                    className="flex items-center justify-center transition 
                              text-red-800 hover:text-red-700
                              shadow hover:shadow-md"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        loading={isDeleting}
        itemName={"this Service "}
      />

      <AdminServiceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleServiceSubmit}
        initialData={editData}
        isEditMode={!!editData}
        isSubmitting={isSubmitting}
        isActionCompleted={isActionCompleted}
      />
    </div>
  );
};

export default Services;
